import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bankImportsService } from '../services/bank-imports.service';

const KEY = 'bank-imports';

export function useBankImports(params: {
  companyId: string;
  bankAccountId?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [KEY, 'list', params],
    queryFn: () => bankImportsService.list(params),
    enabled: !!params.companyId,
  });
}

export function useBankImportSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'one', sessionId],
    queryFn: () => bankImportsService.getOne(sessionId!),
    enabled: !!sessionId,
  });
}

export function useUploadBankImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankAccountId, file }: { bankAccountId: string; file: File }) =>
      bankImportsService.upload(bankAccountId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useApproveBankImport(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { transactionIds: string[]; includeDuplicates: boolean }) =>
      bankImportsService.approve(sessionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'one', sessionId] });
      qc.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useRejectAllBankImport(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => bankImportsService.rejectAll(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'one', sessionId] });
    },
  });
}

export function usePatchBankImportTransaction(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      txId,
      patch,
    }: {
      txId: string;
      patch: Partial<{
        suggestedCategoryId: string | null;
        beneficiaryCompanyId: string | null;
        type: 'INCOME' | 'EXPENSE';
        description: string;
        status: 'REJECTED';
      }>;
    }) => bankImportsService.patchTransaction(sessionId, txId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'one', sessionId] });
    },
  });
}
